/**
 * O tabuleiro do Business Tour: 32 casas, 7 por lado entre os cantos.
 *
 *   0 Início · 8 Ilha · 16 Campeonato Mundial · 24 Volta ao Mundo
 *   cada lado: cidade, cidade, especial, cidade, resort, cidade, cidade
 *   (especial = F total ou Legaaaal nos 3 primeiros lados, Imposto no último)
 *
 * 20 cidades em 8 grupos de cor (2, 3, 2, 3, 2, 3, 2, 3), 4 resorts, 3 cartas, 1 imposto.
 * Os nomes de cada tema ficam fora do motor (themes.ts); aqui só a mecânica.
 */
import { GROUP_PRICES, HOTEL_HOUSES, HOUSE_COST_SHARE, RESORT_PRICE } from './rules';
import type { Level, Square } from './types';

const city = (index: number, group: number, side: number): Square => ({
  index,
  kind: 'city',
  group,
  side,
  price: GROUP_PRICES[group]!,
});
const sq = (index: number, kind: Square['kind'], side?: number): Square => ({
  index,
  kind,
  ...(side === undefined ? {} : { side }),
  ...(kind === 'resort' ? { price: RESORT_PRICE } : {}),
});

export const SQUARES: readonly Square[] = [
  sq(0, 'start'),
  city(1, 1, 0),
  city(2, 1, 0),
  sq(3, 'chance', 0),
  city(4, 2, 0),
  sq(5, 'resort', 0),
  city(6, 2, 0),
  city(7, 2, 0),
  sq(8, 'island'),
  city(9, 3, 1),
  city(10, 3, 1),
  sq(11, 'chance', 1),
  city(12, 4, 1),
  sq(13, 'resort', 1),
  city(14, 4, 1),
  city(15, 4, 1),
  sq(16, 'championship'),
  city(17, 5, 2),
  city(18, 5, 2),
  sq(19, 'chance', 2),
  city(20, 6, 2),
  sq(21, 'resort', 2),
  city(22, 6, 2),
  city(23, 6, 2),
  sq(24, 'worldTour'),
  city(25, 7, 3),
  city(26, 7, 3),
  sq(27, 'tax', 3),
  city(28, 8, 3),
  sq(29, 'resort', 3),
  city(30, 8, 3),
  city(31, 8, 3),
];

export function square(index: number): Square {
  const s = SQUARES[index];
  if (!s) throw new Error(`Casa inexistente: ${index}`);
  return s;
}

export function isOwnable(index: number): boolean {
  const k = square(index).kind;
  return k === 'city' || k === 'resort';
}

export const OWNABLE_SQUARES: readonly number[] = SQUARES.filter((s) => isOwnable(s.index)).map((s) => s.index);
export const CITY_SQUARES: readonly number[] = SQUARES.filter((s) => s.kind === 'city').map((s) => s.index);
export const RESORT_SQUARES: readonly number[] = SQUARES.filter((s) => s.kind === 'resort').map((s) => s.index);

/** Cidades de cada grupo de cor. */
export const GROUPS: Record<number, number[]> = (() => {
  const g: Record<number, number[]> = {};
  for (const s of SQUARES) if (s.kind === 'city' && s.group) (g[s.group] ??= []).push(s.index);
  return g;
})();

/** Casas compráveis de cada lado (cidades e resort), para o monopólio de linha. */
export const SIDES: number[][] = [0, 1, 2, 3].map((side) =>
  SQUARES.filter((s) => s.side === side && isOwnable(s.index)).map((s) => s.index),
);

export function groupOf(index: number): number[] {
  const s = square(index);
  if (s.kind === 'city' && s.group) return GROUPS[s.group]!;
  if (s.kind === 'resort') return [...RESORT_SQUARES];
  return [];
}

export function priceOf(index: number): number {
  return square(index).price ?? 0;
}

const round10 = (n: number) => Math.round(n / 10) * 10;

/** Custo de um nível (0 = bandeirola ... 4 = hotel) numa cidade. */
export function levelCost(index: number, level: number): number {
  const s = square(index);
  if (s.kind === 'resort') return level === 0 ? s.price! : 0;
  if (level === 0) return s.price!;
  const house = round10(s.price! * HOUSE_COST_SHARE);
  return level === 4 ? house * HOTEL_HOUSES : house;
}

/** Quanto custa ir do nível `from` (−1 = sem dono) até `to`. */
export function upgradeCost(index: number, from: number, to: number): number {
  let total = 0;
  for (let l = from + 1; l <= to; l++) total += levelCost(index, l);
  return total;
}

/** Valor investido numa propriedade com este nível (base para imposto, compra alheia e venda). */
export function valueOf(index: number, level: Level | number): number {
  return upgradeCost(index, -1, level);
}
